import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Bot, CircleDot, CircleCheck, MessageSquare, Tag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { wanneer } from "./RepoVastleggingen";

/**
 * EG5 (CA4, opdracht Frank 1 november 2026): issues per repo met titel, tekst, status, labels en opmerkingen.
 *
 * Indeling naar GitHub: een lijst met Open en Gesloten, per issue de labels als kleine kaartjes en klikbaar als filter,
 * "Nieuw issue" met titel, beschrijving en labels, en een issue met de kop (titel, #nummer, status), de labels (te
 * wijzigen), de opmerkingen en Sluiten of Heropenen. API-vorm naar Gitea (`issue.go`).
 */

/** "bug, ui ,, werk" naar ["bug", "ui", "werk"]. De backend maakt ze verder schoon en weigert te lange. PUUR. */
export function labelsUitTekst(tekst) {
  return (tekst || "").split(",").map((l) => l.trim()).filter(Boolean);
}

function Labels({ labels, onKies = null }) {
  if (!labels?.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {labels.map((l) => (
        onKies ? (
          <button key={l} type="button" onClick={(e) => { e.stopPropagation(); onKies(l); }} data-testid={`label-${l}`}
                  className="rounded-full border px-2 text-xs text-muted-foreground hover:bg-muted">{l}</button>
        ) : (
          <span key={l} className="rounded-full border px-2 text-xs text-muted-foreground" data-testid={`label-${l}`}>{l}</span>
        )
      ))}
    </span>
  );
}

export default function RepoIssues({ repo, onLosOp = null }) {
  const { t, taal } = useTaal();
  const [lijst, setLijst] = useState([]);
  const [filter, setFilter] = useState("open");
  const [label, setLabel] = useState("");
  const [nieuw, setNieuw] = useState(false);
  const [vorm, setVorm] = useState({ titel: "", tekst: "", labels: "" });
  const [gekozen, setGekozen] = useState(null);
  const [opmerking, setOpmerking] = useState("");
  const [labelTekst, setLabelTekst] = useState("");
  const basis = `${API}/repos/${repo.id}/issues`;

  const laad = useCallback(() => {
    axios.get(basis).then((r) => setLijst(r.data)).catch(() => setLijst([]));
  }, [basis]);
  useEffect(() => { laad(); }, [laad]);

  const fout = (e, standaard) => {
    const tekst = e?.response?.data?.detail;
    toast.error(typeof tekst === "string" ? tekst : standaard);
  };

  function toon(data) {
    setGekozen(data);
    setLabelTekst(data.labels.join(", "));
  }

  async function open(nummer) {
    try {
      toon((await axios.get(`${basis}/${nummer}`)).data);
    } catch (e) {
      fout(e, t("Het issue kon niet worden geopend."));
    }
  }

  async function maak(e) {
    e.preventDefault();
    try {
      const { data } = await axios.post(basis, { titel: vorm.titel, tekst: vorm.tekst, labels: labelsUitTekst(vorm.labels) });
      setNieuw(false);
      setVorm({ titel: "", tekst: "", labels: "" });
      laad();
      toon(data);
    } catch (err) {
      fout(err, t("Het issue kon niet worden gemaakt."));
    }
  }

  async function zet(status) {
    try {
      toon((await axios.post(`${basis}/${gekozen.nummer}/status`, { status })).data);
      laad();
    } catch (err) {
      fout(err, t("Dat lukte niet."));
    }
  }

  async function bewaarLabels() {
    try {
      toon((await axios.put(`${basis}/${gekozen.nummer}/labels`, { labels: labelsUitTekst(labelTekst) })).data);
      laad();
    } catch (err) {
      fout(err, t("De labels konden niet worden opgeslagen."));
    }
  }

  async function plaats(e) {
    e.preventDefault();
    try {
      await axios.post(`${basis}/${gekozen.nummer}/opmerkingen`, { tekst: opmerking });
      setOpmerking("");
      await open(gekozen.nummer);
    } catch (err) {
      fout(err, t("De opmerking kon niet worden geplaatst."));
    }
  }

  if (gekozen) {
    const isOpen = gekozen.status === "open";
    return (
      <div className="space-y-2" data-testid="issue-detail">
        <button type="button" className="text-sm text-primary hover:underline" onClick={() => { setGekozen(null); laad(); }}>
          {t("Terug naar de issues")}
        </button>
        <div className="rounded-lg border p-3">
          <p className="break-words text-sm font-semibold">{gekozen.titel} <span className="text-muted-foreground">#{gekozen.nummer}</span></p>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <span className={`flex items-center gap-1 font-medium ${isOpen ? "text-emerald-600" : "text-violet-600"}`} data-testid="issue-status">
              {isOpen ? <CircleDot className="h-3.5 w-3.5" /> : <CircleCheck className="h-3.5 w-3.5" />}{t(gekozen.status)}
            </span>
            <Labels labels={gekozen.labels} />
          </p>
        </div>
        <p className="whitespace-pre-wrap break-words rounded-lg border p-3 text-sm">{gekozen.tekst || t("Geen beschrijving.")}</p>
        {/* CA8: de agent werkt dit issue af op een eigen tak en opent de pull request; samenvoegen doe jij. */}
        {isOpen && onLosOp && (
          <Button type="button" size="sm" className="tap-target" onClick={() => onLosOp(gekozen.nummer)} data-testid="issue-los-op">
            <Bot className="h-4 w-4 mr-1" />{t("Laat de agent dit oplossen")}
          </Button>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Tag className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <input value={labelTekst} onChange={(e) => setLabelTekst(e.target.value)} aria-label={t("Labels, met komma's ertussen")}
                 placeholder={t("Labels, met komma's ertussen")} data-testid="issue-labels"
                 className="min-w-0 flex-1 rounded-md border bg-transparent px-2 py-1 text-sm" />
          <Button type="button" size="sm" variant="outline" className="tap-target" onClick={bewaarLabels} data-testid="issue-labels-bewaar">
            {t("Labels opslaan")}
          </Button>
        </div>
        {gekozen.opmerkingen.length > 0 && (
          <ul className="space-y-1" data-testid="issue-opmerkingen">
            {gekozen.opmerkingen.map((o) => (
              <li key={o.id} className="rounded-lg border p-2 text-sm">
                <p className="whitespace-pre-wrap break-words">{o.tekst}</p>
                <p className="text-xs text-muted-foreground">{wanneer(new Date(o.op * 1000).toISOString(), taal)}</p>
              </li>
            ))}
          </ul>
        )}
        <form onSubmit={plaats} className="space-y-1 rounded-lg border p-2" data-testid="issue-opmerking-vorm">
          <textarea value={opmerking} onChange={(e) => setOpmerking(e.target.value)} placeholder={t("Schrijf een opmerking")}
                    aria-label={t("Schrijf een opmerking")} className="min-h-[60px] w-full rounded-md border bg-transparent px-2 py-1 text-sm"
                    data-testid="issue-opmerking-tekst" />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" variant="outline" className="tap-target" disabled={!opmerking.trim()} data-testid="issue-opmerking-plaats">
              <MessageSquare className="h-4 w-4 mr-1" />{t("Opmerking plaatsen")}
            </Button>
            <Button type="button" size="sm" variant="outline" className="tap-target ml-auto" data-testid="issue-sluit"
                    onClick={() => zet(isOpen ? "gesloten" : "open")}>
              {isOpen ? t("Sluit het issue") : t("Heropen het issue")}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  const naLabel = lijst.filter((i) => !label || i.labels.some((l) => l.toLowerCase() === label.toLowerCase()));
  const telling = (id) => naLabel.filter((i) => (id === "open" ? i.status === "open" : i.status !== "open")).length;
  const zichtbaar = naLabel.filter((i) => (filter === "open" ? i.status === "open" : i.status !== "open"));
  return (
    <div className="space-y-2" data-testid="repo-issues">
      <div className="flex flex-wrap items-center gap-2">
        {[["open", t("Open")], ["dicht", t("Gesloten")]].map(([id, naam]) => (
          <button key={id} type="button" onClick={() => setFilter(id)} data-testid={`issues-${id}`}
                  className={`tap-target rounded-md px-2 py-1 text-sm ${filter === id ? "bg-muted font-semibold" : "text-muted-foreground"}`}>
            {naam} ({telling(id)})
          </button>
        ))}
        {label && (
          <button type="button" onClick={() => setLabel("")} data-testid="issues-label-weg"
                  className="rounded-full border px-2 text-xs">{t("Label {label}, wis het filter", { label })}</button>
        )}
        <Button type="button" size="sm" className="tap-target ml-auto" onClick={() => setNieuw(!nieuw)} data-testid="issue-nieuw">
          {t("Nieuw issue")}
        </Button>
      </div>
      {nieuw && (
        <form onSubmit={maak} className="space-y-2 rounded-lg border p-3" data-testid="issue-vorm">
          <input value={vorm.titel} onChange={(e) => setVorm({ ...vorm, titel: e.target.value })} placeholder={t("Titel")}
                 aria-label={t("Titel")} className="w-full rounded-md border bg-transparent px-2 py-1 text-sm" data-testid="issue-titel" />
          <textarea value={vorm.tekst} onChange={(e) => setVorm({ ...vorm, tekst: e.target.value })} placeholder={t("Beschrijving")}
                    aria-label={t("Beschrijving")} className="min-h-[80px] w-full rounded-md border bg-transparent px-2 py-1 text-sm"
                    data-testid="issue-tekst" />
          <input value={vorm.labels} onChange={(e) => setVorm({ ...vorm, labels: e.target.value })}
                 placeholder={t("Labels, met komma's ertussen")} aria-label={t("Labels, met komma's ertussen")}
                 className="w-full rounded-md border bg-transparent px-2 py-1 text-sm" data-testid="issue-vorm-labels" />
          <Button type="submit" size="sm" className="tap-target" disabled={!vorm.titel.trim()} data-testid="issue-maak">
            {t("Issue maken")}
          </Button>
        </form>
      )}
      <ul className="divide-y rounded-lg border" data-testid="issues-lijst">
        {zichtbaar.length === 0 && <li className="px-3 py-2 text-sm text-muted-foreground">{t("Geen issues.")}</li>}
        {zichtbaar.map((i) => (
          <li key={i.nummer} className="flex items-start gap-2 px-3 py-2">
            {i.status === "open"
              ? <CircleDot className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              : <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-violet-600" />}
            <span className="min-w-0 flex-1">
              <button type="button" onClick={() => open(i.nummer)} data-testid={`issue-${i.nummer}`}
                      className="tap-target block break-words text-left text-sm hover:underline">{i.titel}</button>
              <span className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                #{i.nummer} · {wanneer(new Date(i.gemaakt_op * 1000).toISOString(), taal)}
                {i.opmerkingen.length > 0 && <> · <MessageSquare className="h-3 w-3" aria-hidden="true" />{i.opmerkingen.length}</>}
                <Labels labels={i.labels} onKies={setLabel} />
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
